import React from "react";
import { Box, Flex, Icon, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";

export type LegendKind = "metric" | "benchmark" | "attribute" | "measure" | "time";

interface LegendItemProps {
  color: string;
  icon: React.ElementType;
  label: string;
  active: boolean;
  onToggle: () => void;
}

const LegendItem: React.FC<LegendItemProps> = ({ color, icon, label, active, onToggle }) => (
  <Flex
    as="button"
    type="button"
    align="center"
    gap={1.5}
    w="100%"
    py={1}
    px={1.5}
    borderRadius="md"
    cursor="pointer"
    borderWidth="1px"
    borderColor={active ? "purple.300" : "transparent"}
    bg={active ? "purple.50" : "transparent"}
    _hover={{ bg: active ? "purple.100" : "gray.50" }}
    onClick={e => {
      e.stopPropagation();
      onToggle();
    }}
  >
    <Flex
      w={6}
      h={6}
      borderRadius="full"
      bg={color}
      align="center"
      justify="center"
      flexShrink={0}
      opacity={active ? 1 : 0.75}
    >
      <Icon as={icon} color="white" boxSize={3.5} />
    </Flex>
    <Text
      fontSize="xs"
      color={active ? "purple.800" : "gray.600"}
      mb={0}
      fontWeight={active ? "700" : "500"}
      textAlign="left"
    >
      {label}
    </Text>
  </Flex>
);

interface GraphLegendProps {
  showLegend: boolean;
  setShowLegend: (v: boolean) => void;
  legendFilterKinds: LegendKind[];
  toggleLegendKind: (kind: LegendKind) => void;
}

const GraphLegend: React.FC<GraphLegendProps> = ({
  showLegend,
  setShowLegend,
  legendFilterKinds,
  toggleLegendKind,
}) => (
  <Box position="absolute" bottom={4} right={4} zIndex={10}>
    {!showLegend ? (
      <Tooltip label="Show node types" placement="left" hasArrow bg="gray.700" color="white" fontSize="xs">
        <IconButton
          aria-label="Show node types legend"
          icon={<Icon as={AppIcons.layers} boxSize={4} />}
          size="sm"
          borderRadius="lg"
          bg="white"
          color="gray.500"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
          _hover={{ bg: "gray.50", color: "gray.700" }}
          onClick={() => setShowLegend(true)}
        />
      </Tooltip>
    ) : (
      <Box
        bg="white"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
        px={3}
        py={2}
        minW="160px"
        maxW="220px"
      >
        <Flex align="center" justify="space-between" gap={2} mb={0}>
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.600"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={0}
            textAlign="left"
          >
            Node Types
          </Text>
          <Tooltip label="Hide legend" placement="top" hasArrow bg="gray.700" color="white" fontSize="xs">
            <IconButton
              aria-label="Hide node types legend"
              icon={<Icon as={AppIcons.chevronDown} boxSize={4} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              minW={7}
              h={7}
              _hover={{ bg: "gray.100", color: "gray.700" }}
              onClick={() => setShowLegend(false)}
            />
          </Tooltip>
        </Flex>
        <Text fontSize="xs" color="gray.600" mb={2} lineHeight="1.3" textAlign="left">
          (click to filter types)
        </Text>
        <Flex direction="column" gap={0.5}>
          <LegendItem
            color="#6b21a8"
            icon={AppIcons.metric}
            label="Metric"
            active={legendFilterKinds.includes("metric")}
            onToggle={() => toggleLegendKind("metric")}
          />
          <LegendItem
            color="#c2410c"
            icon={AppIcons.benchmark}
            label="Benchmark"
            active={legendFilterKinds.includes("benchmark")}
            onToggle={() => toggleLegendKind("benchmark")}
          />
          <LegendItem
            color="#1d4ed8"
            icon={AppIcons.attribute}
            label="Attribute"
            active={legendFilterKinds.includes("attribute")}
            onToggle={() => toggleLegendKind("attribute")}
          />
          <LegendItem
            color="#15803d"
            icon={AppIcons.measure}
            label="Measure"
            active={legendFilterKinds.includes("measure")}
            onToggle={() => toggleLegendKind("measure")}
          />
          <LegendItem
            color="#a16207"
            icon={AppIcons.timeframe}
            label="Time"
            active={legendFilterKinds.includes("time")}
            onToggle={() => toggleLegendKind("time")}
          />
        </Flex>
      </Box>
    )}
  </Box>
);

export default GraphLegend;
